import { useState } from "react";
import { toast } from "sonner";
import { CalendarOff, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useClinics } from "@/hooks/useClinics";
import { api, formatApiError, formatTime, prettyDate } from "@/lib/api";

const describe = (lv, clinics) => {
  const dates = lv.start_date === lv.end_date ? prettyDate(lv.start_date) : `${prettyDate(lv.start_date)} – ${prettyDate(lv.end_date)}`;
  const hours = lv.start_time ? `${formatTime(lv.start_time)} – ${formatTime(lv.end_time)}` : "Whole day";
  const where = lv.clinics.length === clinics.length ? "All clinics" : lv.clinics.map((id) => clinics.find((c) => c.id === id)?.name || id).join(", ");
  return { dates, hours, where };
};

export const LeaveList = ({ leaves, onChanged }) => {
  const clinics = useClinics();
  const [busy, setBusy] = useState(null);

  const remove = async (id) => {
    setBusy(id);
    try {
      await api.delete(`/admin/leaves/${id}`);
      toast.success("Leave removed. Those times are open for booking again.");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  if (!leaves.length) return null;
  return (
    <section className="bg-white rounded-3xl border border-slate-100 p-4 sm:p-6" data-testid="leave-list">
      <h2 className="flex items-center gap-2 text-base md:text-lg font-semibold text-[#1A252C]"><CalendarOff className="h-5 w-5 text-[#DC2626]" /> Upcoming leave</h2>
      <ul className="mt-4 divide-y divide-slate-100">
        {leaves.map((lv) => {
          const d = describe(lv, clinics);
          return (
            <li key={lv.id} className="py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`leave-item-${lv.id}`}>
              <div className="text-sm">
                <p className="font-semibold text-[#1A252C]">{d.dates} · {d.hours}</p>
                <p className="text-muted-foreground">{d.where}{lv.note ? ` · ${lv.note}` : ""}</p>
              </div>
              <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" disabled={busy === lv.id} onClick={() => remove(lv.id)} data-testid={`leave-delete-${lv.id}`}>
                <Trash2 className="mr-1 h-4 w-4" /> Remove
              </Button>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
